/* eslint-disable jsx-a11y/label-has-associated-control */
import {
  CloudArrowUpIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { DocumentIcon } from '@heroicons/react/24/solid';
import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/catalyst/button';
import { Text } from '@/components/ui/catalyst/text';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import {
  useCreateBlobMutation,
  useGetBlobsQuery,
} from '@/services/api/blobService';
import { useGetCollectionTreeQuery } from '@/services/api/collectionService';
import { useUploadPackageMutation } from '@/services/api/hipaaPackageService';

import { useRequestPage } from '../contexts/RequestPageContext';

interface ManualUploadPanelProps {
  onComplete: () => void;
  onCancel?: () => void;
}

const TAB_OPTIONS = {
  UPLOAD: 'upload',
  SELECT: 'select',
} as const;

type TabOption = (typeof TAB_OPTIONS)[keyof typeof TAB_OPTIONS];

/**
 * Upload panel for HIPAA packages with upload and select options
 * Uses context for all data to avoid prop drilling
 */
export const ManualUploadPanel: React.FC<ManualUploadPanelProps> = ({
  onComplete,
  onCancel,
}) => {
  const { requestInfo } = useRequestPage();
  const [uploadPackage, { isLoading: isSaving }] = useUploadPackageMutation();
  const [createBlob, { isLoading: isUploadingBlob }] = useCreateBlobMutation();
  const { data: blobs } = useGetBlobsQuery(requestInfo.matterId);
  const { data: collectionTree } = useGetCollectionTreeQuery(
    requestInfo.matterId,
  );

  const [activeTab, setActiveTab] = useState<TabOption>(TAB_OPTIONS.UPLOAD);
  const [selectedBlobId, setSelectedBlobId] = useState<string | null>(null);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Filter to only show PDF blobs
  const pdfBlobs = useMemo(() => {
    if (!blobs) return [];
    return blobs.filter(
      (blob) =>
        blob.file_mime_type === 'application/pdf' ||
        blob.file_extension === '.pdf',
    );
  }, [blobs]);

  const handleUploadFile = useCallback(async (file: File) => {
    if (file.type !== 'application/pdf') {
      toast.error('Please select a PDF file');
      return;
    }

    setUploadedFile(file);
  }, []);

  const handleFileSelect = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const { files } = event.target;
      if (files && files.length > 0) {
        handleUploadFile(files[0]);
      }
    },
    [handleUploadFile],
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  }, []);

  const handleDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);

      const file = e.dataTransfer.files[0];
      if (file) {
        await handleUploadFile(file);
      }
    },
    [handleUploadFile],
  );

  const handleSave = useCallback(async () => {
    let blobIdToUse = selectedBlobId;

    // If in upload tab and we have a file, create a blob first
    if (activeTab === TAB_OPTIONS.UPLOAD && uploadedFile) {
      try {
        const rootCollection = collectionTree?.id;
        if (!rootCollection) {
          toast.error('Unable to determine collection');
          return;
        }

        // Create FormData instead of JSON with base64
        const formData = new FormData();
        formData.append('file', uploadedFile);
        formData.append('name', uploadedFile.name.replace('.pdf', ''));
        formData.append('original_filename', uploadedFile.name);
        formData.append('collection', rootCollection);
        formData.append('file_mime_type', 'application/pdf');

        const createdBlob = await createBlob({
          matterId: requestInfo.matterId,
          blob: formData as any,
        }).unwrap();

        blobIdToUse = createdBlob.id;
      } catch (error) {
        toast.error('Failed to upload file');
        return;
      }
    }

    if (!blobIdToUse) {
      toast.error('Please select or upload a document');
      return;
    }

    try {
      await uploadPackage({
        ...requestInfo,
        body: {
          blob_id: blobIdToUse,
        },
      }).unwrap();

      toast.success('Package uploaded successfully');
      onComplete();
    } catch (error) {
      toast.error('Failed to set package');
    }
  }, [
    activeTab,
    uploadedFile,
    selectedBlobId,
    collectionTree,
    createBlob,
    uploadPackage,
    requestInfo,
    onComplete,
  ]);

  const selectedBlob = useMemo(() => {
    if (!selectedBlobId || !blobs) return null;
    return blobs.find((b) => b.id === selectedBlobId) || null;
  }, [selectedBlobId, blobs]);

  const isLoading = isUploadingBlob || isSaving;
  const canSave =
    (activeTab === TAB_OPTIONS.UPLOAD && uploadedFile) ||
    (activeTab === TAB_OPTIONS.SELECT && selectedBlobId);

  return (
    <div className="space-y-6">
      <Text className="text-sm text-gray-600">
        Upload a pre-completed HIPAA package PDF or select one from your
        documents
      </Text>

      {/* Warning Box */}
      <div className="border rounded-lg p-4 bg-amber-50 border-amber-200">
        <div className="flex gap-3">
          <ExclamationTriangleIcon
            className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5"
            aria-hidden="true"
          />
          <div className="flex-1">
            <p className="font-medium text-amber-900">
              Note about uploaded packages:
            </p>
            <p className="text-sm text-amber-700 mt-1">
              Uploaded packages cannot be broken down into individual templates.
              For editable components, use the &quot;Generate Package&quot;
              option instead.
            </p>
          </div>
        </div>
      </div>

      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as TabOption)}
      >
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value={TAB_OPTIONS.UPLOAD}>Upload New</TabsTrigger>
          <TabsTrigger value={TAB_OPTIONS.SELECT}>Select Existing</TabsTrigger>
        </TabsList>

        <TabsContent value={TAB_OPTIONS.UPLOAD}>
          <div
            className={cn(
              'border-2 border-dashed rounded-lg transition-all duration-200 p-12',
              isDragging
                ? 'border-indigo-500 bg-indigo-50 scale-[0.99]'
                : 'border-gray-300 hover:border-gray-400',
            )}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <div className="flex flex-col items-center justify-center">
              <CloudArrowUpIcon
                className={cn(
                  'h-16 w-16 mb-4 transition-colors',
                  isDragging ? 'text-indigo-500' : 'text-gray-400',
                )}
                aria-hidden="true"
              />
              <p className="text-lg font-medium text-gray-700 mb-2">
                {isDragging
                  ? 'Drop file here'
                  : 'Drag and drop a PDF file here'}
              </p>
              <p className="text-sm text-gray-500 mb-4">or</p>
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={isLoading}
              >
                {isUploadingBlob ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                    Browse Files
                  </>
                )}
              </Button>
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileSelect}
                className="hidden"
                accept=".pdf,application/pdf"
              />
              <p className="text-xs text-gray-400 mt-4">PDF files only</p>
            </div>
          </div>

          {uploadedFile && (
            <div className="mt-4 flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <DocumentIcon className="h-8 w-8 text-red-500" />
                <div>
                  <Text className="font-medium">{uploadedFile.name}</Text>
                  <Text className="text-xs text-gray-500">
                    {(uploadedFile.size / 1024).toFixed(2)} KB
                  </Text>
                </div>
              </div>
              <Button
                plain
                onClick={() => setUploadedFile(null)}
                className="text-gray-500 hover:text-gray-700"
              >
                Remove
              </Button>
            </div>
          )}
        </TabsContent>

        <TabsContent value={TAB_OPTIONS.SELECT}>
          <div className="space-y-2 max-h-96 overflow-y-auto border rounded-lg p-4">
            {pdfBlobs.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                No PDF documents found. Upload one in the &quot;Upload New&quot;
                tab.
              </div>
            ) : (
              pdfBlobs.map((blob) => (
                <button
                  key={blob.id}
                  type="button"
                  onClick={() => setSelectedBlobId(blob.id)}
                  className={cn(
                    'w-full flex items-center gap-3 p-3 rounded-lg border transition-colors',
                    selectedBlobId === blob.id
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50',
                  )}
                >
                  <DocumentIcon
                    className={cn(
                      'h-8 w-8',
                      selectedBlobId === blob.id
                        ? 'text-indigo-600'
                        : 'text-red-500',
                    )}
                  />
                  <div className="flex-1 text-left">
                    <div className="font-medium text-gray-900">{blob.name}</div>
                    <div className="text-xs text-gray-500">
                      {blob.original_filename || 'No filename'} •{' '}
                      {(blob.file_size / 1024).toFixed(2)} KB
                    </div>
                  </div>
                  {selectedBlobId === blob.id && (
                    <div className="flex-shrink-0">
                      <div className="w-5 h-5 rounded-full bg-indigo-600 flex items-center justify-center">
                        <svg
                          className="w-3 h-3 text-white"
                          fill="currentColor"
                          viewBox="0 0 12 12"
                        >
                          <path d="M3.707 5.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4a1 1 0 00-1.414-1.414L5 6.586 3.707 5.293z" />
                        </svg>
                      </div>
                    </div>
                  )}
                </button>
              ))
            )}
          </div>

          {selectedBlob && (
            <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
              <div className="flex items-center gap-2">
                <DocumentIcon className="h-5 w-5 text-green-600" />
                <span className="text-sm font-medium text-green-900">
                  Selected: {selectedBlob.name}
                </span>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>

      <div className="flex justify-end gap-3 pt-4">
        {onCancel && (
          <Button onClick={onCancel} plain disabled={isLoading}>
            Cancel
          </Button>
        )}
        <Button
          onClick={handleSave}
          disabled={!canSave || isLoading}
          color="indigo"
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2" />
              {isUploadingBlob ? 'Uploading...' : 'Saving...'}
            </>
          ) : (
            'Use This Package'
          )}
        </Button>
      </div>
    </div>
  );
};
